import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Clients — the health plans a third-party administrator serves. Each client
 * is its own workspace: departments, people, documents and audits inside it
 * never mix with another client's.
 *
 * Used in two places with the same behavior:
 *   - the TPA's own People page (the access administrator adds and removes
 *     the plans it serves);
 *   - the platform console (platform support does the same for any TPA,
 *     pass tpaId), because each client is billed.
 *
 * Removing a client closes its workspace and ends staff access there. Data
 * is kept and the client can be restored. Permanent deletion stays with
 * platform support through the lifecycle console.
 */
export default function ClientsTab({ showToast, tpaId = null }) {
  const navigate = useNavigate();
  const q = tpaId ? `?tpa_id=${tpaId}` : '';
  const [clients, setClients] = useState(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [contract, setContract] = useState('');
  const [pbps, setPbps] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, cms_contract_number }
  const [removing, setRemoving] = useState(null); // client awaiting confirmation

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/v2/tpa/clients${q}`, { credentials: 'include' });
      const data = await res.json();
      setClients(data.success ? data.clients : []);
    } catch {
      setClients([]);
    }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const send = async (method, path, body) => {
    const res = await fetch(`${API}/api/admin/v2/tpa${path}${q}`, {
      method, credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    return res.json();
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await send('POST', '/clients', { name: name.trim(), cms_contract_number: contract.trim() || null, pbp_ids: pbps.trim() });
      if (!data.success) { showToast('error', 'Could not add client', data.message); return; }
      showToast('success', 'Client added', `${data.client.name} is ready. Next: set up its departments, then give staff access.`);
      setName(''); setContract(''); setPbps(''); setAdding(false);
      await load();
    } catch (err) {
      showToast('error', 'Could not add client', err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await send('PATCH', `/clients/${editing.id}`, {
        name: editing.name.trim(),
        cms_contract_number: (editing.cms_contract_number || '').trim() || null,
      });
      if (!data.success) { showToast('error', 'Could not save', data.message); return; }
      showToast('success', 'Client updated');
      setEditing(null);
      await load();
    } catch (err) {
      showToast('error', 'Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const c = removing;
    setBusy(true);
    try {
      const data = await send('DELETE', `/clients/${c.id}`, {});
      if (!data.success) { showToast('error', 'Could not remove client', data.message); return; }
      showToast('success', 'Client removed', `${c.name} is closed. ${data.staff_access_ended || 0} staff access${data.staff_access_ended === 1 ? '' : 'es'} ended. Data is kept and the client can be restored.`);
      setRemoving(null);
      await load();
    } catch (err) {
      showToast('error', 'Could not remove client', err.message);
    } finally {
      setBusy(false);
    }
  };

  const restore = async (c) => {
    setBusy(true);
    try {
      const data = await send('POST', `/clients/${c.id}/restore`, {});
      if (!data.success) { showToast('error', 'Could not restore client', data.message); return; }
      showToast('success', 'Client restored', `${c.name} is open again. Give staff access under Staff access.`);
      await load();
    } catch (err) {
      showToast('error', 'Could not restore client', err.message);
    } finally {
      setBusy(false);
    }
  };

  const isActive = (c) => (c.lifecycle_status || 'active') === 'active';
  const active = (clients || []).filter(isActive);
  const removed = (clients || []).filter((c) => !isActive(c));

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-white">Clients</h2>
          <p className="text-sm text-slate-400 max-w-2xl">
            One workspace per health plan. Everything inside a client stays with that client.
          </p>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white text-sm font-medium shadow-lg shadow-orange-500/20 transition"
        >
          {adding ? 'Cancel' : '+ Add client'}
        </button>
      </div>

      {adding && (
        <form onSubmit={add} className="mb-6 rounded-2xl border border-slate-700 bg-slate-800/60 p-5 grid gap-4 md:grid-cols-[1fr_160px_200px_auto] items-end">
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Health plan name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={120}
              placeholder="e.g. Solis Health Plans"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:border-sky-500 focus:outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">CMS contract</span>
            <input value={contract} onChange={(e) => setContract(e.target.value.toUpperCase())} maxLength={5}
              placeholder="H0982"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:border-sky-500 focus:outline-none font-mono" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">PBPs</span>
            <input value={pbps} onChange={(e) => setPbps(e.target.value)}
              placeholder="001, 002"
              title="The plan benefit packages under this contract. Departments are assigned to them."
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:border-sky-500 focus:outline-none font-mono" />
          </label>
          <button type="submit" disabled={busy}
            className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium disabled:opacity-60">
            {busy ? 'Adding…' : 'Add'}
          </button>
        </form>
      )}

      {clients === null ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 px-6 py-10 text-center">
          <p className="text-slate-300 font-medium">No clients yet</p>
          <p className="text-sm text-slate-500 mt-1">Add the first health plan. You can add more at any time.</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-700/60 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-900/40 border-b border-slate-700/50">
                <tr>
                  <Th>Client</Th><Th>Contract</Th><Th>Departments</Th><Th>Staff with access</Th><Th>Setup</Th><Th right>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {active.map((c) => (
                  <tr key={c.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition">
                    <td className="px-5 py-3.5">
                      {editing?.id === c.id ? (
                        <form onSubmit={saveEdit} className="flex items-center gap-2">
                          <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white w-56" />
                          <input value={editing.cms_contract_number || ''} onChange={(e) => setEditing({ ...editing, cms_contract_number: e.target.value.toUpperCase() })}
                            maxLength={5} placeholder="H0000"
                            className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white w-20 font-mono" />
                          <button type="submit" disabled={busy} className="text-xs text-sky-300 hover:text-white">Save</button>
                          <button type="button" onClick={() => setEditing(null)} className="text-xs text-slate-400 hover:text-white">Cancel</button>
                        </form>
                      ) : (
                        <span className="font-medium text-white">{c.name}</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-slate-300 font-mono text-sm">{c.cms_contract_number || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-300">{c.department_count}</td>
                    <td className="px-5 py-3.5 text-slate-300">{c.staff_count}</td>
                    <td className="px-5 py-3.5">
                      {c.setup_complete
                        ? <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-full border border-emerald-500/20">Complete</span>
                        : <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded-full border border-amber-500/20">Pending</span>}
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <button onClick={() => navigate(`/people?org=${c.id}`)} className="text-xs text-sky-300 hover:text-white mr-4">Departments &amp; people</button>
                      <button onClick={() => setEditing({ id: c.id, name: c.name, cms_contract_number: c.cms_contract_number || '' })} className="text-xs text-slate-400 hover:text-white mr-4">Rename</button>
                      <button onClick={() => setRemoving(c)} className="text-xs text-red-300 hover:text-red-200">Remove</button>
                    </td>
                  </tr>
                ))}
                {active.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-6 text-sm text-slate-500 text-center">No active clients.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {removed.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-300 mb-2">Removed clients</h3>
              <p className="text-xs text-slate-500 mb-3">Closed workspaces. Their data is kept. Restore to reopen, then grant staff access again.</p>
              <div className="rounded-2xl border border-slate-700/40 overflow-hidden">
                <table className="w-full">
                  <tbody>
                    {removed.map((c) => (
                      <tr key={c.id} className="border-b border-slate-700/30">
                        <td className="px-5 py-3 text-slate-400">{c.name}</td>
                        <td className="px-5 py-3 text-slate-500 font-mono text-sm">{c.cms_contract_number || '—'}</td>
                        <td className="px-5 py-3">
                          <span className="text-xs bg-slate-700/60 text-slate-300 px-2 py-1 rounded-full border border-slate-600">Removed</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => restore(c)} disabled={busy} className="text-xs text-sky-300 hover:text-white disabled:opacity-60">Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {removing && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl bg-slate-800 border border-slate-600 shadow-2xl p-6 space-y-4">
            <h3 className="text-base font-semibold text-white">Remove {removing.name}?</h3>
            <p className="text-sm text-slate-300">
              The workspace closes and staff access to it ends right away. Nothing is deleted: the client's data is kept and it can be restored later.
            </p>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={() => setRemoving(null)} className="text-sm text-slate-400 hover:text-white">Keep client</button>
              <button type="button" onClick={remove} disabled={busy}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-60">
                {busy ? 'Removing…' : 'Remove client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th className={`${right ? 'text-right' : 'text-left'} text-xs uppercase tracking-wide text-slate-400 font-semibold px-5 py-3`}>{children}</th>
  );
}
